/**
 * SoundDebugWindow — in-game audio debug card (windowed).
 *
 * Self-contained: creates a fresh IAudioEngine via the factory, so it works on
 * any surface (including PTR) without coupling to the live game audio spine. The
 * panel mirrors the PretestLoader Sound Test card but lives in the debug window
 * framework and can be opened from the DebugToolbox.
 */
import React, { useEffect, useMemo, useState } from 'react';
import { allCueIds, resolveCue } from '@/game/systems/audio/cues/SoundBank';
import { BUS_NAMES, type BusName, type Cue } from '@/game/systems/audio/engine/audioTypes';
import type { CueLogEntry } from '@/game/systems/audio/engine/IAudioEngine';
import { createAudioEngine } from '@/game/systems/audio/engine/audioEngineFactory';
import { WasmAudioEngine } from '@/game/systems/audio/engine/WasmAudioEngine';
import { COLORS, SPACE, FONT_SIZE, FONT_WEIGHT, RADII, FONTS, panelSurface, textStyle, monoText, statusColor } from '../theme';

const CUE_LOG_SHOWN = 8;
const POLL_MS = 250;

const sectionStyle: React.CSSProperties = {
  marginBottom: SPACE.md,
};

const rowStyle: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: SPACE.sm,
  padding: `${SPACE.xxs}px 0`,
};

const buttonStyle: React.CSSProperties = {
  padding: `${SPACE.xs}px ${SPACE.md}px`,
  borderRadius: RADII.md,
  border: `1px solid ${COLORS.border}`,
  background: COLORS.surfaceRaised,
  color: COLORS.textPrimary,
  cursor: 'pointer',
  fontSize: FONT_SIZE.sm,
};

const logStyle: React.CSSProperties = {
  ...panelSurface(),
  background: COLORS.surfaceSunken,
  padding: SPACE.sm,
  ...monoText('xs'),
  color: COLORS.textSecondary,
  maxHeight: 160,
  overflowY: 'auto',
  whiteSpace: 'pre-wrap',
};

function useCuesByBus(): Record<BusName, Cue[]> {
  return useMemo(() => {
    const cues = allCueIds()
      .map((id) => resolveCue(id))
      .filter((c): c is Cue => c != null);
    const byBus = {} as Record<BusName, Cue[]>;
    for (const bus of BUS_NAMES) byBus[bus] = [];
    for (const cue of cues) byBus[cue.bus].push(cue);
    return byBus;
  }, []);
}

export const SoundDebugWindow: React.FC = () => {
  const { engine, kind, available } = useMemo(() => createAudioEngine(), []);
  const isWasmEngine = engine instanceof WasmAudioEngine;
  const cuesByBus = useCuesByBus();

  const [busGain, setBusGain] = useState<Record<BusName, number>>(() => {
    const init = {} as Record<BusName, number>;
    for (const bus of BUS_NAMES) init[bus] = 1;
    return init;
  });
  const [resumed, setResumed] = useState(false);
  const [log, setLog] = useState<CueLogEntry[]>([]);
  const [bankIds, setBankIds] = useState<string[]>([]);

  useEffect(() => {
    if (!isWasmEngine) return;
    const timer = window.setInterval(() => {
      const ids = engine.bankCueIds();
      if (ids.length !== bankIds.length) setBankIds(ids);
    }, POLL_MS);
    return () => window.clearInterval(timer);
  }, [engine, isWasmEngine, bankIds.length]);

  useEffect(() => {
    const timer = window.setInterval(() => {
      const drained = engine.drainCueLog();
      if (drained.length) {
        setLog((prev) => [...drained, ...prev].slice(0, CUE_LOG_SHOWN));
      }
    }, POLL_MS);
    return () => window.clearInterval(timer);
  }, [engine]);

  useEffect(() => {
    return () => {
      try {
        engine.dispose();
      } catch { /* ignore */ }
    };
  }, [engine]);

  const handleResume = async () => {
    await engine.resume();
    setResumed(true);
  };

  const handleBusGain = (bus: BusName, value: number) => {
    setBusGain((prev) => ({ ...prev, [bus]: value }));
    engine.setBusGain(bus, value);
  };

  const indicator = available ? kind.toUpperCase() : `${kind.toUpperCase()} (unavailable)`;
  const indicatorColor = available ? statusColor('ok') : statusColor('warn');

  return (
    <div style={{ ...panelSurface(), padding: SPACE.md, fontFamily: FONTS.ui, minHeight: 320 }}>
      <div style={{ ...sectionStyle, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <div style={{ fontWeight: FONT_WEIGHT.semibold, fontSize: FONT_SIZE.md, color: COLORS.textPrimary }}>
          Sound Debug
        </div>
        <div style={{ fontSize: FONT_SIZE.xs, color: indicatorColor }}>{indicator}</div>
      </div>

      <div style={sectionStyle}>
        <button type="button" onClick={handleResume} style={buttonStyle}>
          {resumed ? '🔊 audio resumed' : '▶ resume audio'}
        </button>
      </div>

      <div style={sectionStyle}>
        <div style={{ ...textStyle('muted', 'xs'), marginBottom: SPACE.xs }}>BUS GAINS</div>
        {BUS_NAMES.map((bus) => (
          <div key={bus} style={rowStyle}>
            <span style={{ width: 56, color: COLORS.textSecondary }}>{bus}</span>
            <input
              type="range" min={0} max={1} step={0.01} value={busGain[bus]}
              onChange={(e) => handleBusGain(bus, Number(e.target.value))}
              style={{ flex: 1, accentColor: COLORS.info }}
              aria-label={`${bus} bus gain`}
            />
            <span style={{ width: 32, textAlign: 'right', ...textStyle('muted', 'xs') }}>
              {busGain[bus].toFixed(2)}
            </span>
          </div>
        ))}
      </div>

      <div style={sectionStyle}>
        <div style={{ ...textStyle('muted', 'xs'), marginBottom: SPACE.xs }}>CUES</div>
        {BUS_NAMES.filter((bus) => cuesByBus[bus].length > 0).map((bus) => (
          <div key={bus} style={{ marginBottom: SPACE.sm }}>
            <div style={{ ...textStyle('muted', 'xs'), textTransform: 'uppercase' }}>{bus}</div>
            {cuesByBus[bus].map((cue) => (
              <div key={cue.id} style={rowStyle}>
                <button type="button" onClick={() => engine.fire(cue.id)} style={buttonStyle}>▶ Fire</button>
                <span style={{ flex: 1, color: COLORS.textPrimary }}>{cue.id}</span>
                <span style={{ fontSize: FONT_SIZE.xs, color: COLORS.textMuted }}>p{cue.priority.toFixed(2)}</span>
              </div>
            ))}
          </div>
        ))}
        {isWasmEngine && (
          <div style={{ marginBottom: SPACE.sm }}>
            <div style={{ ...textStyle('muted', 'xs'), textTransform: 'uppercase' }}>
              bank {bankIds.length === 0 ? '· loading…' : `· ${bankIds.length} loaded`}
            </div>
            {bankIds.length === 0 && (
              <div style={{ ...textStyle('muted', 'xs') }}>— sampled bank loading —</div>
            )}
            {bankIds.map((id) => (
              <div key={id} style={rowStyle}>
                <button type="button" onClick={() => engine.fire(id)} style={buttonStyle}>▶ Fire</button>
                <span style={{ flex: 1, color: COLORS.textPrimary }}>{id}</span>
                <span style={{ fontSize: FONT_SIZE.xs, color: COLORS.textMuted }}>sample</span>
              </div>
            ))}
          </div>
        )}
      </div>

      <div>
        <div style={{ ...textStyle('muted', 'xs'), marginBottom: SPACE.xs }}>CUE LOG</div>
        <div style={logStyle}>
          {log.length === 0
            ? '— no cues fired yet —'
            : log.map((e, i) => (
              <div key={`${e.t}-${i}`}>
                {new Date(e.t).toLocaleTimeString()} · {e.cueId} [{e.bus}]{e.stolen ? ' (stole voice)' : ''}
              </div>
            ))}
        </div>
      </div>
    </div>
  );
};
